import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { IEquifaxPrequalConfig } from "@prescreen/lib/equifax-prequal"
import { IEquifaxDitConfig } from "@prescreen/lib/equifax-dit-config"
import {
  Integration,
  type IJackHenrySilverlakeIntegrationConfig,
  type IJackHenrySynergyIntegrationConfig,
  JACK_HENRY_SYNERGY_OAUTH_SCOPE,
} from "@prescreen/lib/types"
import { apiFetch } from "@prescreen/lib/api-client"
import { useNavigate } from "react-router-dom"
import { useState, useEffect, type ReactNode } from "react"
import { mask, isMasked } from "@prescreen/lib/masking"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Info } from "lucide-react"
import { useSession } from "../contexts/SessionContext"
import {
  canManageIntegrationStoredFields,
  JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS,
  JACK_HENRY_SYNERGY_REQUEST_CONTEXT_FIELD_HEADERS,
} from "@prescreen/lib/integrations/request-context-db-fields"
import {
  OE_HEADER_APPLICATION_ID,
  OE_HEADER_BASE_URL,
  OE_HEADER_CONFIG_ID,
  OE_HEADER_ENTITY_ID,
  OE_HEADER_MEMBER_NUMBER,
  OE_HEADER_MODEL_NUMBER,
  OE_HEADER_OAUTH_CLIENT_ASSERTION,
  OE_HEADER_OAUTH_CLIENT_ID,
  OE_HEADER_OAUTH_CLIENT_SECRET,
  OE_HEADER_SECURITY_CODE,
  OE_HEADER_TENANT_ID,
} from "@prescreen/lib/integrations/oe-headers"
import {
  integrationHasJackHenrySilverlake as integrationHasSilverlake,
  integrationHasJackHenrySynergy,
} from "@/lib/oauth-secret-scope-builder"
import IntegrationProviderOptionCard from "@/components/IntegrationProviderOptionCard"
import {
  integrationHasProvider,
  integrationProviderIdsFromProductToggles,
} from "@prescreen/lib/integrations/integration-providers"
import type { IntegrationProviderId } from "@prescreen/lib/types/integration-provider-id"

function requestContextFieldTooltip(headerName: string): string {
  return `Optional integration value used when API callers omit the ${headerName} request header.`
}
function FieldLabel({
  htmlFor,
  required,
  tooltip,
  children,
  labelClassName,
}: {
  htmlFor?: string
  required?: boolean
  tooltip: ReactNode
  children: ReactNode
  labelClassName?: string
}) {
  const ariaName =
    typeof children === "string"
      ? `More about: ${children}`
      : htmlFor
        ? `More about: ${htmlFor}`
        : "More about this field"
  const labelBody = (
    <>
      {children}
      {required ? (
        <span className="text-destructive ml-0.5" aria-hidden="true">
          *
        </span>
      ) : null}
    </>
  )
  return (
    <div className="flex items-center gap-1.5">
      {htmlFor ? (
        <Label htmlFor={htmlFor} className={labelClassName ?? "mb-0"}>
          {labelBody}
        </Label>
      ) : (
        <span className={labelClassName ?? "text-sm font-medium"}>{labelBody}</span>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex shrink-0 rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={ariaName}
          >
            <Info className="size-4" strokeWidth={1.5} aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          align="start"
          className="max-w-xs text-left text-xs leading-snug"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </div>
  )
}

type JackHenryFormFields = Pick<
  IJackHenrySilverlakeIntegrationConfig,
  | "oauthClientId"
  | "privateKey"
  | "routingId"
  | "institutionEnvironment"
  | "validConsumerName"
  | "validConsumerProduct"
  | "auditUserId"
  | "auditWorkstationId"
>

type JackHenrySynergyFormFields = Pick<
  IJackHenrySynergyIntegrationConfig,
  "clientId" | "clientSecret" | "institutionId" | "institutionEnvironment"
>

function emptyJackHenryForm(): Record<keyof JackHenryFormFields, string> {
  return {
    oauthClientId: "",
    privateKey: "",
    routingId: "",
    institutionEnvironment: "",
    validConsumerName: "",
    validConsumerProduct: "",
    auditUserId: "",
    auditWorkstationId: "",
  }
}

function emptyJackHenrySynergyForm(): Record<keyof JackHenrySynergyFormFields, string> {
  return {
    clientId: "",
    clientSecret: "",
    institutionId: "",
    institutionEnvironment: "",
  }
}

function integrationOptionsFromIntegration(i: Integration): IntegrationOptionsState {
  return {
    equifaxPrequal: integrationHasProvider(i, "equifax_prequal"),
    equifaxDit: integrationHasProvider(i, "equifax_dit"),
    silverlake: integrationHasProvider(i, "jack_henry_silverlake"),
    synergy: integrationHasProvider(i, "jack_henry_synergy"),
  }
}

function buildJackHenryRequestPayload(
  form: Record<keyof JackHenryFormFields, string> | undefined,
  isEdit: boolean,
  includeRequestContextFields: boolean
): Partial<IJackHenrySilverlakeIntegrationConfig> | undefined {
  if (!form) {
    return undefined
  }
  const out: Partial<IJackHenrySilverlakeIntegrationConfig> = {}
  const optionalStringKeys: (keyof JackHenryFormFields)[] = [
    "oauthClientId",
    ...(includeRequestContextFields
      ? ([
          "routingId",
          "institutionEnvironment",
          "validConsumerName",
          "validConsumerProduct",
          "auditUserId",
          "auditWorkstationId",
        ] as const)
      : []),
  ]
  for (const key of optionalStringKeys) {
    const v = form[key]
    if (typeof v === "string" && v.trim()) {
      out[key] = v.trim() as never
    }
  }
  const pk = form.privateKey
  if (typeof pk === "string") {
    if (pk.trim() && !isMasked(pk)) {
      out.privateKey = pk
    } else if (pk.trim() && isMasked(pk) && isEdit) {
      out.privateKey = pk
    }
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function buildJackHenrySynergyRequestPayload(
  form: Record<keyof JackHenrySynergyFormFields, string> | undefined,
  isEdit: boolean,
  includeRequestContextFields: boolean
): Partial<IJackHenrySynergyIntegrationConfig> | undefined {
  if (!form) {
    return undefined
  }
  const out: Partial<IJackHenrySynergyIntegrationConfig> = {}
  const stringKeys: (keyof JackHenrySynergyFormFields)[] = [
    "clientId",
    ...(includeRequestContextFields
      ? (["institutionId", "institutionEnvironment"] as const)
      : []),
  ]
  for (const key of stringKeys) {
    const v = form[key]
    if (typeof v === "string" && v.trim()) {
      out[key] = v.trim() as never
    }
  }

  const secret = form.clientSecret
  if (typeof secret === "string") {
    if (secret.trim() && !isMasked(secret)) {
      out.clientSecret = secret
    } else if (secret.trim() && isMasked(secret) && isEdit) {
      out.clientSecret = secret
    }
  }

  if (Object.keys(out).length > 0) {
    out.scope = JACK_HENRY_SYNERGY_OAUTH_SCOPE
  }

  return Object.keys(out).length > 0 ? out : undefined
}

interface IntegrationFormProps {
  integration?: Integration | null; // If provided, we're in edit mode
  onIntegrationSaved: (integration: Integration) => void;
  onCancel?: () => void; // For edit mode
}

type IntegrationFormData = Partial<
  Omit<
    Integration,
    | 'jackHenrySilverlakeConfig'
    | 'bankingProviderId'
    | 'equifaxPrequalConfig'
    | 'equifaxDITConfig'
  >
> & {
  equifaxPrequalConfig: Partial<IEquifaxPrequalConfig>
  equifaxDITConfig: Partial<IEquifaxDitConfig>
  jackHenrySilverlakeConfig: Record<keyof JackHenryFormFields, string>
  jackHenrySynergyConfig: Record<keyof JackHenrySynergyFormFields, string>
  bankingProviderId: string
}

type IntegrationOptionsState = {
  equifaxPrequal: boolean
  equifaxDit: boolean
  silverlake: boolean
  synergy: boolean
}

const EMPTY_INTEGRATION_OPTIONS: IntegrationOptionsState = {
  equifaxPrequal: false,
  equifaxDit: false,
  silverlake: false,
  synergy: false,
}

type IntegrationProviderOptionConfig = {
  providerId: IntegrationProviderId
  stateKey: keyof IntegrationOptionsState
  title: string
  description: string
}

const INTEGRATION_PROVIDER_OPTIONS: IntegrationProviderOptionConfig[] = [
  {
    providerId: "equifax_prequal",
    stateKey: "equifaxPrequal",
    title: "Equifax Prequal",
    description: "Soft credit prequalification APIs.",
  },
  {
    providerId: "equifax_dit",
    stateKey: "equifaxDit",
    title: "Equifax DIT",
    description: "Decision-in-the-loop (DIT) credit decisioning APIs.",
  },
  {
    providerId: "jack_henry_silverlake",
    stateKey: "silverlake",
    title: "Jack Henry Silverlake",
    description: "Core banking APIs—customers, accounts, transfers, cards, and loans.",
  },
  {
    providerId: "jack_henry_synergy",
    stateKey: "synergy",
    title: "Jack Henry Synergy",
    description: "Document upload and archived document APIs.",
  },
]

export default function IntegrationForm({
  integration, 
  onIntegrationSaved, 
  onCancel 
}: IntegrationFormProps) {
  const isEditMode = !!integration
  const navigate = useNavigate()
  const { user } = useSession()
  const isAdministrator = canManageIntegrationStoredFields(user?.role)

  const [integrationFormData, setIntegrationFormData] = useState<IntegrationFormData>({
    name: "",
    description: "",
    callbackUrl: "",
    returnUrl: "",
    equifaxPrequalConfig: {
      clientId: "",
      clientSecret: "",
      baseUrl: "",
      memberNo: "",
      securityCode: "",
      modelNo: "",
    },
    equifaxDITConfig: {
      clientId: "",
      clientSecret: "",
      baseUrl: "",
      tenantId: "",
      applicationId: "",
      configId: "",
      entityId: "",
    },
    bankingProviderId: "",
    jackHenrySilverlakeConfig: emptyJackHenryForm(),
    jackHenrySynergyConfig: emptyJackHenrySynergyForm(),
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [integrationOptions, setIntegrationOptions] =
    useState<IntegrationOptionsState>(EMPTY_INTEGRATION_OPTIONS)

  // Initialize form data when integration prop changes (edit mode)
  useEffect(() => {
    if (integration) {
      setIntegrationOptions(integrationOptionsFromIntegration(integration))
      setIntegrationFormData({
        name: integration.name || "",
        description: integration.description || "",
        callbackUrl: integration.callbackUrl || "",
        returnUrl: integration.returnUrl || "",
        equifaxPrequalConfig: {
          clientId: integration.equifaxPrequalConfig?.clientId || "",
          clientSecret: integration.equifaxPrequalConfig?.clientSecret 
            ? mask(integration.equifaxPrequalConfig.clientSecret, 4)
            : "",
          baseUrl: integration.equifaxPrequalConfig?.baseUrl || "",
          memberNo: integration.equifaxPrequalConfig?.memberNo || "",
          securityCode: integration.equifaxPrequalConfig?.securityCode 
            ? mask(integration.equifaxPrequalConfig.securityCode, 4)
            : "",
          modelNo: integration.equifaxPrequalConfig?.modelNo || "",
        },
        equifaxDITConfig: {
          clientId: integration.equifaxDITConfig?.clientId || "",
          clientSecret: integration.equifaxDITConfig?.clientSecret 
            ? mask(integration.equifaxDITConfig.clientSecret, 4)
            : "",
          baseUrl: integration.equifaxDITConfig?.baseUrl || "",
          tenantId: integration.equifaxDITConfig?.tenantId || "",
          applicationId: integration.equifaxDITConfig?.applicationId || "",
          configId: integration.equifaxDITConfig?.configId || "",
          entityId: integration.equifaxDITConfig?.entityId || "",
        },
        bankingProviderId: integration.bankingProviderId || "",
        jackHenrySilverlakeConfig: (() => {
          const jh = emptyJackHenryForm()
          const c = integration.jackHenrySilverlakeConfig
          if (!c) {
            return jh
          }
          return {
            oauthClientId: c.oauthClientId || "",
            privateKey: c.privateKey ? mask(c.privateKey, 4) : "",
            routingId: c.routingId || "",
            institutionEnvironment: c.institutionEnvironment || "",
            validConsumerName: c.validConsumerName || "",
            validConsumerProduct: c.validConsumerProduct || "",
            auditUserId: c.auditUserId || "",
            auditWorkstationId: c.auditWorkstationId || "",
          }
        })(),
        jackHenrySynergyConfig: (() => {
          const synergy = emptyJackHenrySynergyForm()
          const c = integration.jackHenrySynergyConfig
          if (!c) {
            return synergy
          }
          return {
            clientId: c.clientId || "",
            clientSecret: c.clientSecret ? mask(c.clientSecret, 4) : "",
            institutionId: c.institutionId || "",
            institutionEnvironment: c.institutionEnvironment || "",
          }
        })(),
      })
    } else {
      setIntegrationOptions(EMPTY_INTEGRATION_OPTIONS)
    }
  }, [integration])

  // Helper function to update form data
  const updateFormData = (field: string, value: string) => {
    if (field.startsWith('equifaxPrequalConfig.')) {
      const equifaxField = field.replace('equifaxPrequalConfig.', '')
      setIntegrationFormData(prev => ({
        ...prev,
        equifaxPrequalConfig: {
          ...prev.equifaxPrequalConfig,
          [equifaxField]: value
        }
      }))
    } else if (field.startsWith('equifaxDITConfig.')) {
      const ditField = field.replace('equifaxDITConfig.', '')
      setIntegrationFormData(prev => ({
        ...prev,
        equifaxDITConfig: {
          ...prev.equifaxDITConfig,
          [ditField]: value
        }
      }))
    } else if (field.startsWith("jackHenrySilverlakeConfig.")) {
      const jhField = field.replace(
        "jackHenrySilverlakeConfig.",
        ""
      ) as keyof JackHenryFormFields
      setIntegrationFormData(prev => ({
        ...prev,
        jackHenrySilverlakeConfig: {
          ...emptyJackHenryForm(),
          ...prev.jackHenrySilverlakeConfig,
          [jhField]: value,
        },
      }))
    } else if (field.startsWith("jackHenrySynergyConfig.")) {
      const synergyField = field.replace(
        "jackHenrySynergyConfig.",
        ""
      ) as keyof JackHenrySynergyFormFields
      setIntegrationFormData(prev => ({
        ...prev,
        jackHenrySynergyConfig: {
          ...emptyJackHenrySynergyForm(),
          ...prev.jackHenrySynergyConfig,
          [synergyField]: value,
        },
      }))
    } else {
      setIntegrationFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const toggleIntegrationOption = (key: keyof IntegrationOptionsState) => {
    setIntegrationOptions((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const saveIntegration = async () => {
    try {
      setIsSubmitting(true)

      const silverlakeSelected = integrationOptions.silverlake
      const synergySelected = integrationOptions.synergy
      const equifaxPrequalSelected = integrationOptions.equifaxPrequal
      const equifaxDitSelected = integrationOptions.equifaxDit

      const requestBody: any = {
        name: integrationFormData.name,
        description: integrationFormData.description || undefined,
        callbackUrl: integrationFormData.callbackUrl || undefined,
        returnUrl: integrationFormData.returnUrl || undefined,
        integrations: integrationProviderIdsFromProductToggles(integrationOptions),
      }

      if (isEditMode && integration) {
        if (!silverlakeSelected && integrationHasSilverlake(integration)) {
          requestBody.jackHenrySilverlakeConfig = null
        }
        if (!synergySelected && integrationHasJackHenrySynergy(integration)) {
          requestBody.jackHenrySynergyConfig = null
        }

        if (!equifaxPrequalSelected && integrationHasProvider(integration, "equifax_prequal")) {
          requestBody.equifaxPrequalConfig = null
        }
        if (!equifaxDitSelected && integrationHasProvider(integration, "equifax_dit")) {
          requestBody.equifaxDITConfig = null
        }
      }

      const equifaxConfig = integrationFormData.equifaxPrequalConfig
      const ditConfig = integrationFormData.equifaxDITConfig

      if (equifaxPrequalSelected) {
        if (isAdministrator) {
          requestBody.equifaxPrequalConfig = {
            clientId: equifaxConfig.clientId?.trim() || undefined,
            clientSecret:
              equifaxConfig.clientSecret && !isMasked(equifaxConfig.clientSecret)
                ? equifaxConfig.clientSecret
                : undefined,
            baseUrl: equifaxConfig.baseUrl?.trim() || undefined,
            memberNo: equifaxConfig.memberNo?.trim() || undefined,
            securityCode:
              equifaxConfig.securityCode && !isMasked(equifaxConfig.securityCode)
                ? equifaxConfig.securityCode
                : undefined,
            modelNo: equifaxConfig.modelNo?.trim() || undefined,
          }
        } else if (!isEditMode || !integrationHasProvider(integration, "equifax_prequal")) {
          requestBody.equifaxPrequalConfig = {}
        }
      }

      if (equifaxDitSelected) {
        if (isAdministrator) {
          requestBody.equifaxDITConfig = {
            clientId: ditConfig.clientId?.trim() || undefined,
            clientSecret:
              ditConfig.clientSecret && !isMasked(ditConfig.clientSecret)
                ? ditConfig.clientSecret
                : undefined,
            baseUrl: ditConfig.baseUrl?.trim() || undefined,
            tenantId: ditConfig.tenantId?.trim() || undefined,
            applicationId: ditConfig.applicationId?.trim() || undefined,
            configId: ditConfig.configId?.trim() || undefined,
            entityId: ditConfig.entityId?.trim() || undefined,
          }
        } else if (!isEditMode || !integrationHasProvider(integration, "equifax_dit")) {
          requestBody.equifaxDITConfig = {}
        }
      }


      if (isAdministrator) {
        const jhPayload = buildJackHenryRequestPayload(
          integrationFormData.jackHenrySilverlakeConfig,
          isEditMode,
          true
        )
        const synergyPayload = buildJackHenrySynergyRequestPayload(
          integrationFormData.jackHenrySynergyConfig,
          isEditMode,
          true
        )

        if (silverlakeSelected) {
          if (jhPayload) {
            requestBody.jackHenrySilverlakeConfig = jhPayload
          } else if (!isEditMode || !integration || !integrationHasSilverlake(integration)) {
            requestBody.jackHenrySilverlakeConfig = undefined
          }
        }

        if (synergySelected) {
          if (synergyPayload) {
            requestBody.jackHenrySynergyConfig = synergyPayload
          } else if (!isEditMode || !integration || !integrationHasJackHenrySynergy(integration)) {
            requestBody.jackHenrySynergyConfig = undefined
          }
        }
      }

      const url = isEditMode ? `/api/integrations/${integration._id}` : '/api/integrations'
      const method = isEditMode ? 'PUT' : 'POST'

      const response = await apiFetch(url, {
        method,
        body: JSON.stringify(requestBody),
      })

      if (response.ok) {
        const json = await response.json()
        if (json.success && json.data?.integration) {
          const savedIntegration = json.data.integration
          onIntegrationSaved(savedIntegration)
          
          if (isEditMode) {
            // For edit mode, just call onCancel to close the form
            onCancel?.()
          } else {
            const createdId =
              typeof savedIntegration._id === "string" ? savedIntegration._id.trim() : ""
            navigate(
              "/integrations",
              createdId ? { state: { highlightIntegrationId: createdId } } : undefined,
            )
            // Reset form
            setIntegrationFormData({
              name: "",
              description: "",
              callbackUrl: "",
              returnUrl: "",
              equifaxPrequalConfig: {
                clientId: "",
                clientSecret: "",
                baseUrl: "",
                memberNo: "",
                securityCode: "",
                modelNo: "",
              },
              equifaxDITConfig: {
                clientId: "",
                clientSecret: "",
                baseUrl: "",
                tenantId: "",
                applicationId: "",
                configId: "",
                entityId: "",
              },
              bankingProviderId: "",
              jackHenrySilverlakeConfig: emptyJackHenryForm(),
              jackHenrySynergyConfig: emptyJackHenrySynergyForm(),
            })
            setIntegrationOptions(EMPTY_INTEGRATION_OPTIONS)
          }
        } else {
          alert(`Error: ${json.error || 'Unknown error'}`)
        }
      } else {
        const error = await response.json()
        alert(`Error: ${error.error}`)
      }
    } catch (error) {
      console.error('Error saving integration:', error)
      alert(`Failed to ${isEditMode ? 'update' : 'create'} integration`)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (isEditMode) {
      onCancel?.()
    } else {
      // Reset form for create mode
      setIntegrationFormData({
        name: "",
        description: "",
        callbackUrl: "",
        returnUrl: "",
        equifaxPrequalConfig: {
          clientId: "",
          clientSecret: "",
          baseUrl: "",
          memberNo: "",
          securityCode: "",
          modelNo: "",
        },
        equifaxDITConfig: {
          clientId: "",
          clientSecret: "",
          baseUrl: "",
          tenantId: "",
          applicationId: "",
          configId: "",
          entityId: "",
        },
        bankingProviderId: "",
        jackHenrySilverlakeConfig: emptyJackHenryForm(),
        jackHenrySynergyConfig: emptyJackHenrySynergyForm(),
      })
      setIntegrationOptions(EMPTY_INTEGRATION_OPTIONS)
    }
  }

  const silverlakeSelected = integrationOptions.silverlake
  const synergySelected = integrationOptions.synergy

  const anyIntegrationOptionSelected = INTEGRATION_PROVIDER_OPTIONS.some(
    (option) => integrationOptions[option.stateKey]
  )

  const canSubmit =
    Boolean(integrationFormData.name?.trim()) &&
    (isEditMode || anyIntegrationOptionSelected)

  return (
    <TooltipProvider delayDuration={200}>
      <>
      <Card>
        <CardHeader>
          <CardTitle>
            {isEditMode ? 'Edit Integration' : 'Create Integration'}
          </CardTitle>
          <CardDescription>
            {isEditMode
              ? "Update your integration settings"
              : isAdministrator
                ? "Configure Equifax, banking, Synergy, and related credentials for one financial institution."
                : "Choose which products to enable and set basic integration details. Stored credentials are configured by administrators."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-gray-900">Integration options</h3>
            <p className="text-sm text-gray-600">
              Select one or more products for this financial institution. Each card lists the
              request headers required for that service—open the info icon for details.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              {INTEGRATION_PROVIDER_OPTIONS.map((option) => (
                <IntegrationProviderOptionCard
                  key={option.providerId}
                  providerId={option.providerId}
                  title={option.title}
                  description={option.description}
                  selected={integrationOptions[option.stateKey]}
                  onToggle={() => toggleIntegrationOption(option.stateKey)}
                />
              ))}
            </div>
            {!isEditMode && !anyIntegrationOptionSelected && (
              <p className="text-sm text-amber-700">
                Select at least one product to create an integration.
              </p>
            )}
          </div>

          {/* Basic Integration Configuration */}
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900">Basic Configuration</h3>
            
            <div className="space-y-2">
              <FieldLabel
                htmlFor="integrationName"
                required
                tooltip="Short display name for this integration in the admin UI. Required to create or update."
              >
                Integration name
              </FieldLabel>
              <Input
                id="integrationName"
                placeholder="Enter integration name"
                value={integrationFormData.name || ""}
                onChange={(e) => updateFormData('name', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <FieldLabel
                htmlFor="description"
                tooltip="Optional notes for your team. This is not sent to Equifax or Jack Henry."
              >
                Description
              </FieldLabel>
              <Textarea
                id="description"
                placeholder="Enter integration description (optional)"
                value={integrationFormData.description || ""}
                onChange={(e) => updateFormData('description', e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <FieldLabel
                htmlFor="callbackUrl"
                tooltip="OAuth redirect URI if your application uses an authorization-code flow with this integration. Use a full https URL when set."
              >
                Callback URL
              </FieldLabel>
              <Input
                id="callbackUrl"
                placeholder="https://your-app.com/callback (optional)"
                value={integrationFormData.callbackUrl || ""}
                onChange={(e) => updateFormData('callbackUrl', e.target.value)}
                type="url"
              />
            </div>

            <div className="space-y-2">
              <FieldLabel
                htmlFor="returnUrl"
                tooltip="Optional URL to send end users after flows that reference a return URL. Use a full https URL when set."
              >
                Return URL
              </FieldLabel>
              <Input
                id="returnUrl"
                placeholder="https://your-app.com/return (optional)"
                value={integrationFormData.returnUrl || ""}
                onChange={(e) => updateFormData('returnUrl', e.target.value)}
                type="url"
              />
            </div>
          </div>

          {/* Equifax Prequal Configuration */}
          {isAdministrator && integrationOptions.equifaxPrequal && (
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900">Equifax Prequal Configuration</h3>
            <p className="text-sm text-gray-600">
              Optional stored values for Equifax prequal. At request time, send matching{" "}
              <code className="text-xs bg-muted px-1 rounded">x-oe-*</code> headers or configure
              values here.
              {isEditMode && (
                <span className="text-amber-600 ml-2">
                  Leave secret fields empty to keep current values.
                </span>
              )}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FieldLabel
                  htmlFor="equifaxClientId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_ID)}
                >
                  Equifax client ID
                </FieldLabel>
                <Input
                  id="equifaxClientId"
                  placeholder="Equifax client ID"
                  value={integrationFormData.equifaxPrequalConfig?.clientId || ""}
                  onChange={(e) => updateFormData('equifaxPrequalConfig.clientId', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="equifaxClientSecret"
                  tooltip={`${requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_SECRET)} Stored encrypted.`}
                >
                  Equifax client secret
                </FieldLabel>
                <Input
                  id="equifaxClientSecret"
                  placeholder={isEditMode ? "Leave empty to keep current" : "Equifax client secret"}
                  value={integrationFormData.equifaxPrequalConfig?.clientSecret || ""}
                  onChange={(e) => updateFormData('equifaxPrequalConfig.clientSecret', e.target.value)}
                />
                {isEditMode && integrationFormData.equifaxPrequalConfig?.clientSecret && isMasked(integrationFormData.equifaxPrequalConfig.clientSecret) && (
                  <p className="text-xs text-gray-500">
                    Current value is masked. Enter a new value to update.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="equifaxBaseUrl"
                  tooltip={requestContextFieldTooltip(OE_HEADER_BASE_URL)}
                >
                  Equifax base URL
                </FieldLabel>
                <Input
                  id="equifaxBaseUrl"
                  placeholder="https://api.equifax.com"
                  value={integrationFormData.equifaxPrequalConfig?.baseUrl || ""}
                  onChange={(e) => updateFormData('equifaxPrequalConfig.baseUrl', e.target.value)}
                  type="url"
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="equifaxMemberNo"
                  tooltip={requestContextFieldTooltip(OE_HEADER_MEMBER_NUMBER)}
                >
                  Member number
                </FieldLabel>
                <Input
                  id="equifaxMemberNo"
                  placeholder="Equifax member number"
                  value={integrationFormData.equifaxPrequalConfig?.memberNo || ""}
                  onChange={(e) => updateFormData('equifaxPrequalConfig.memberNo', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="equifaxSecurityCode"
                  tooltip={`${requestContextFieldTooltip(OE_HEADER_SECURITY_CODE)} Stored encrypted.`}
                >
                  Security code
                </FieldLabel>
                <Input
                  id="equifaxSecurityCode"
                  placeholder={isEditMode ? "Leave empty to keep current" : "Equifax security code"}
                  value={integrationFormData.equifaxPrequalConfig?.securityCode || ""}
                  onChange={(e) => updateFormData('equifaxPrequalConfig.securityCode', e.target.value)}
                />
                {isEditMode && integrationFormData.equifaxPrequalConfig?.securityCode && isMasked(integrationFormData.equifaxPrequalConfig.securityCode) && (
                  <p className="text-xs text-gray-500">
                    Current value is masked. Enter a new value to update.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="equifaxModelNo"
                  tooltip={requestContextFieldTooltip(OE_HEADER_MODEL_NUMBER)}
                >
                  Model number
                </FieldLabel>
                <Input
                  id="equifaxModelNo"
                  placeholder="Equifax model number"
                  value={integrationFormData.equifaxPrequalConfig?.modelNo || ""}
                  onChange={(e) => updateFormData('equifaxPrequalConfig.modelNo', e.target.value)}
                />
              </div>
            </div>
          </div>
          )}

          {/* Equifax DIT Configuration */}
          {isAdministrator && integrationOptions.equifaxDit && (
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900">Equifax DIT Configuration</h3>
            <p className="text-sm text-gray-600">
              Optional stored values for Equifax DIT. At request time, send matching{" "}
              <code className="text-xs bg-muted px-1 rounded">x-oe-*</code> headers or configure
              values here.
              {isEditMode && (
                <span className="text-amber-600 ml-2">
                  Leave secret fields empty to keep current values.
                </span>
              )}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditClientId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_ID)}
                >
                  Equifax client ID
                </FieldLabel>
                <Input
                  id="ditClientId"
                  placeholder="Equifax client ID"
                  value={integrationFormData.equifaxDITConfig?.clientId || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.clientId', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditClientSecret"
                  tooltip={`${requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_SECRET)} Stored encrypted.`}
                >
                  Equifax client secret
                </FieldLabel>
                <Input
                  id="ditClientSecret"
                  placeholder={isEditMode ? "Leave empty to keep current" : "Equifax client secret"}
                  value={integrationFormData.equifaxDITConfig?.clientSecret || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.clientSecret', e.target.value)}
                />
                {isEditMode && integrationFormData.equifaxDITConfig?.clientSecret && isMasked(integrationFormData.equifaxDITConfig.clientSecret) && (
                  <p className="text-xs text-gray-500">
                    Current value is masked. Enter a new value to update.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditBaseUrl"
                  tooltip={requestContextFieldTooltip(OE_HEADER_BASE_URL)}
                >
                  Equifax base URL
                </FieldLabel>
                <Input
                  id="ditBaseUrl"
                  placeholder="https://api.equifax.com"
                  value={integrationFormData.equifaxDITConfig?.baseUrl || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.baseUrl', e.target.value)}
                  type="url"
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditTenantId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_TENANT_ID)}
                >
                  Tenant ID
                </FieldLabel>
                <Input
                  id="ditTenantId"
                  placeholder="Equifax tenant ID"
                  value={integrationFormData.equifaxDITConfig?.tenantId || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.tenantId', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditApplicationId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_APPLICATION_ID)}
                >
                  Application ID
                </FieldLabel>
                <Input
                  id="ditApplicationId"
                  placeholder="Equifax application ID"
                  value={integrationFormData.equifaxDITConfig?.applicationId || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.applicationId', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditConfigId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_CONFIG_ID)}
                >
                  Config ID
                </FieldLabel>
                <Input
                  id="ditConfigId"
                  placeholder="Equifax config ID"
                  value={integrationFormData.equifaxDITConfig?.configId || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.configId', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <FieldLabel
                  htmlFor="ditEntityId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_ENTITY_ID)}
                >
                  Entity ID
                </FieldLabel>
                <Input
                  id="ditEntityId"
                  placeholder="Equifax entity ID"
                  value={integrationFormData.equifaxDITConfig?.entityId || ""}
                  onChange={(e) => updateFormData('equifaxDITConfig.entityId', e.target.value)}
                />
              </div>
            </div>
          </div>
          )}

          {isAdministrator && silverlakeSelected && (
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900">Jack Henry Silverlake (BaaS)</h3>
            <p className="text-sm text-gray-600">
              Optional stored values for Jack Henry Silverlake. Fixed Jack Henry host URLs use platform
              defaults. At request time, send matching{" "}
              <code className="text-xs bg-muted px-1 rounded">x-oe-*</code> headers
              {isAdministrator
                ? " or configure values below when partners omit those headers."
                : "."}{" "}
              <span className="font-medium">BaaS permission tiers are set per OAuth secret</span> when
              you create the integration or add secrets—not here.
              {isEditMode && (
                <span className="text-amber-600 ml-2">
                  Leave the private key empty to keep the current value.
                </span>
              )}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FieldLabel
                  htmlFor="jhOauthClientId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_ID)}
                >
                  OAuth client ID
                </FieldLabel>
                <Input
                  id="jhOauthClientId"
                  value={integrationFormData.jackHenrySilverlakeConfig?.oauthClientId ?? ""}
                  onChange={(e) =>
                    updateFormData("jackHenrySilverlakeConfig.oauthClientId", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <FieldLabel
                  htmlFor="jhPrivateKey"
                  tooltip={`Optional integration PEM used when API callers omit the ${OE_HEADER_OAUTH_CLIENT_ASSERTION} request header. Stored encrypted.`}
                >
                  Private key (PEM)
                </FieldLabel>
                <Textarea
                  id="jhPrivateKey"
                  rows={6}
                  className="font-mono text-xs"
                  placeholder={
                    isEditMode ? "Leave empty to keep current encrypted key" : "-----BEGIN PRIVATE KEY-----"
                  }
                  value={integrationFormData.jackHenrySilverlakeConfig?.privateKey ?? ""}
                  onChange={(e) =>
                    updateFormData("jackHenrySilverlakeConfig.privateKey", e.target.value)
                  }
                />
                {isEditMode &&
                  integrationFormData.jackHenrySilverlakeConfig?.privateKey &&
                  isMasked(integrationFormData.jackHenrySilverlakeConfig.privateKey) && (
                    <p className="text-xs text-gray-500">
                      Current value is masked. Paste a new PEM to rotate the key.
                    </p>
                  )}
              </div>
              {isAdministrator ? (
                <>
                  <div className="md:col-span-2 space-y-1 pt-2">
                    <h4 className="text-sm font-medium text-gray-900">Request context (administrator)</h4>
                    <p className="text-xs text-gray-600">
                      Optional values stored on the integration when API callers omit matching{" "}
                      <code className="text-xs bg-muted px-1 rounded">x-oe-*</code> headers.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhRoutingId"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS.routingId
                      )}
                    >
                      Routing ID
                    </FieldLabel>
                    <Input
                      id="jhRoutingId"
                      value={integrationFormData.jackHenrySilverlakeConfig?.routingId ?? ""}
                      onChange={(e) =>
                        updateFormData("jackHenrySilverlakeConfig.routingId", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhInstitutionEnvironment"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS.institutionEnvironment
                      )}
                    >
                      Institution environment
                    </FieldLabel>
                    <Input
                      id="jhInstitutionEnvironment"
                      value={
                        integrationFormData.jackHenrySilverlakeConfig?.institutionEnvironment ?? ""
                      }
                      onChange={(e) =>
                        updateFormData(
                          "jackHenrySilverlakeConfig.institutionEnvironment",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhValidConsumerName"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS.validConsumerName
                      )}
                    >
                      Valid consumer name
                    </FieldLabel>
                    <Input
                      id="jhValidConsumerName"
                      value={integrationFormData.jackHenrySilverlakeConfig?.validConsumerName ?? ""}
                      onChange={(e) =>
                        updateFormData("jackHenrySilverlakeConfig.validConsumerName", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhValidConsumerProduct"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS.validConsumerProduct
                      )}
                    >
                      Valid consumer product
                    </FieldLabel>
                    <Input
                      id="jhValidConsumerProduct"
                      value={
                        integrationFormData.jackHenrySilverlakeConfig?.validConsumerProduct ?? ""
                      }
                      onChange={(e) =>
                        updateFormData(
                          "jackHenrySilverlakeConfig.validConsumerProduct",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhAuditUserId"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS.auditUserId
                      )}
                    >
                      Audit user ID
                    </FieldLabel>
                    <Input
                      id="jhAuditUserId"
                      value={integrationFormData.jackHenrySilverlakeConfig?.auditUserId ?? ""}
                      onChange={(e) =>
                        updateFormData("jackHenrySilverlakeConfig.auditUserId", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhAuditWorkstationId"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SILVERLAKE_REQUEST_CONTEXT_FIELD_HEADERS.auditWorkstationId
                      )}
                    >
                      Audit workstation ID
                    </FieldLabel>
                    <Input
                      id="jhAuditWorkstationId"
                      value={
                        integrationFormData.jackHenrySilverlakeConfig?.auditWorkstationId ?? ""
                      }
                      onChange={(e) =>
                        updateFormData("jackHenrySilverlakeConfig.auditWorkstationId", e.target.value)
                      }
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
          )}

          {isAdministrator && synergySelected && (
          <div className="space-y-4 border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900">Jack Henry Synergy (Document APIs)</h3>
            <p className="text-sm text-gray-600">
              Optional stored values for Jack Henry Synergy document APIs (
              <code className="text-xs bg-muted px-1 rounded">/v1/documents</code>). Synergy host URLs
              and product id are fixed in the platform. At request time, send matching{" "}
              <code className="text-xs bg-muted px-1 rounded">x-oe-*</code> headers
              {isAdministrator
                ? " or configure values below when partners omit those headers."
                : "."}
              {isEditMode && (
                <span className="text-amber-600 ml-2">
                  Leave client secret empty to keep the current value.
                </span>
              )}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FieldLabel
                  htmlFor="jhSynergyClientId"
                  tooltip={requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_ID)}
                >
                  Client ID
                </FieldLabel>
                <Input
                  id="jhSynergyClientId"
                  value={integrationFormData.jackHenrySynergyConfig?.clientId ?? ""}
                  onChange={(e) =>
                    updateFormData("jackHenrySynergyConfig.clientId", e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <FieldLabel
                  htmlFor="jhSynergyClientSecret"
                  tooltip={`${requestContextFieldTooltip(OE_HEADER_OAUTH_CLIENT_SECRET)} Stored encrypted.`}
                >
                  Client secret
                </FieldLabel>
                <Input
                  id="jhSynergyClientSecret"
                  placeholder={isEditMode ? "Leave empty to keep current" : ""}
                  value={integrationFormData.jackHenrySynergyConfig?.clientSecret ?? ""}
                  onChange={(e) =>
                    updateFormData("jackHenrySynergyConfig.clientSecret", e.target.value)
                  }
                />
                {isEditMode &&
                  integrationFormData.jackHenrySynergyConfig?.clientSecret &&
                  isMasked(integrationFormData.jackHenrySynergyConfig.clientSecret) && (
                    <p className="text-xs text-gray-500">
                      Current value is masked. Enter a new value to rotate the secret.
                    </p>
                  )}
              </div>
              {isAdministrator ? (
                <>
                  <div className="md:col-span-2 space-y-1 pt-2">
                    <h4 className="text-sm font-medium text-gray-900">Request context (administrator)</h4>
                    <p className="text-xs text-gray-600">
                      Optional values stored on the integration when API callers omit matching{" "}
                      <code className="text-xs bg-muted px-1 rounded">x-oe-*</code> headers.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhSynergyInstitutionId"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SYNERGY_REQUEST_CONTEXT_FIELD_HEADERS.institutionId
                      )}
                    >
                      Institution ID
                    </FieldLabel>
                    <Input
                      id="jhSynergyInstitutionId"
                      value={integrationFormData.jackHenrySynergyConfig?.institutionId ?? ""}
                      onChange={(e) =>
                        updateFormData("jackHenrySynergyConfig.institutionId", e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <FieldLabel
                      htmlFor="jhSynergyInstitutionEnvironment"
                      tooltip={requestContextFieldTooltip(
                        JACK_HENRY_SYNERGY_REQUEST_CONTEXT_FIELD_HEADERS.institutionEnvironment
                      )}
                    >
                      Institution environment
                    </FieldLabel>
                    <Input
                      id="jhSynergyInstitutionEnvironment"
                      value={
                        integrationFormData.jackHenrySynergyConfig?.institutionEnvironment ?? ""
                      }
                      onChange={(e) =>
                        updateFormData(
                          "jackHenrySynergyConfig.institutionEnvironment",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </>
              ) : null}
            </div>
          </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            {isEditMode && (
              <Button 
                variant="outline" 
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
            <Button 
              onClick={saveIntegration} 
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting 
                ? (isEditMode ? 'Updating...' : 'Creating...') 
                : (isEditMode ? 'Update Integration' : 'Create Integration')
              }
            </Button>
          </div>
        </CardContent>
      </Card>

      </>
    </TooltipProvider>
  )
}
